import type { TreeData } from '@revelio/ui';

import { GEO_TAG_TO_SELECTION_LIST_KEY } from './geoTaxonomyLevels';
import type { TaxonomyLevelMetadata } from './taxonomyTree';
import {
  type ParentLinkedItem,
  treeToParentLinkedSelectionLists,
} from './treeToParentLinkedSelectionLists';

/**
 * Geography variant of `treeToParentLinkedSelectionLists`: re-keys the tree's
 * level tags onto the canonical FE selection-list keys via
 * `GEO_TAG_TO_SELECTION_LIST_KEY`, so consumers read `msa` / `city` / `county`
 * whatever the backend calls those levels. The original tag keys are preserved
 * alongside the aliases so tag-keyed consumers keep working.
 */
export const treeToGeoSelectionLists = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[]
): Partial<Record<string, ParentLinkedItem[]>> => {
  const linked = treeToParentLinkedSelectionLists(treeData, levels);
  const aliased: Partial<Record<string, ParentLinkedItem[]>> = { ...linked };

  for (const [tag, key] of Object.entries(GEO_TAG_TO_SELECTION_LIST_KEY)) {
    const list = linked[tag];
    // Don't let an aliased tag clobber a list the tree already published under
    // the canonical key — a tree using the legacy `msa` tag must not be
    // overwritten by an empty `metro_area` entry, or vice versa.
    if (list !== undefined && aliased[key] === undefined) {
      aliased[key] = list;
    }
  }

  return aliased;
};
